import BigNumber from "bignumber.js";
import React, { Component, forwardRef, Fragment, useEffect, useImperativeHandle, useReducer, useRef, useState } from "react";
import { GetOrderType, OrderStatus, OrderTypes, SearchTimeScope, WAIdToName, WalletAccount } from "../conf/Config";
import DataCenter, { CurrentCurrency, QuoteCoinID } from "../modules/DataCenter";
import { Listbox, Transition } from '@headlessui/react';
import { animated, useSpring } from "react-spring";
import { CheckIcon, SelectorIcon, ArrowLeftIcon, ArrowNarrowLeftIcon, ArrowNarrowDownIcon, ArrowsExpandIcon, ArrowCircleUpIcon, ArrowSmLeftIcon, ArrowUpIcon, ArrowDownIcon, ArrowNarrowUpIcon, ChevronRightIcon, CheckCircleIcon, ChevronDownIcon, MinusIcon, PlusIcon, XIcon, ArrowSmRightIcon, ArrowRightIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/react/solid'
import TabMenu from "./TabMenu";
import { request } from "../utils/request";
import API from "../conf/API";
import { Events, JSEvent } from "../utils/JSEvent";
import { LongTimeToDateStr, LongTimeToFullStr, LongTimeToTimeStr, trimZero } from "../utils/Utils";
import Button from "./control/Button";
import ErrorCode from "../modules/Error";
import { LoadingSVG } from "./Svgs";
import { WalletService } from "../modules/WalletService"
import { usePopWindow } from "./BottomPopWindow";
import { ListSelector } from "./ListSelector";
import { Localize } from "../modules/localize/Localize";

function getCoinImage(coinFull) {
    let icon = null;
    try {
        icon = require(`../images/coins/${coinFull}.svg`);
    } catch (e) {
        try {
            icon = require(`../images/coins/${coinFull}.png`);
        } catch (e) {
            icon = require(`../images/icon-trade.svg`);
        }
    }

    icon = icon.default == null ? icon : icon.default;
    return icon;
}

export const CoinIcon = ({ coin, className }) => {
    const coinFull = coin == null ? "" : coin.fullName;
    let icon = getCoinImage(coinFull);
    return <img className={className + " rounded-full"} src={icon} />
}

/**
 * 余额标签
 * props: {
 * 
 *      balance: 当前余额(BigNumber)
 *      decimals: 当前余额保留的小数位数 
 *      exchangeRate: 兑换汇率，需要显示法币金额时设置
 *      show: 0 = 显示货币，1=显示法币
 *      coinSymbol: null 不显示，否则在货币数字前追加货币符号，例如 $
 *      maskBalance: false = 正常显示，true=隐藏实际余额，显示为******
 * }
 */
export class BalanceLabel extends Component {
    constructor(props) {
        super(props);
    }

    render() {

        let { balance, decimals, exchangeRate, show, maskBalance, coinSymbol } = this.props;

        if (balance == null) balance = new BigNumber(0);
        if (decimals == null) decimals = 2;
        if (exchangeRate == null) exchangeRate = 1;
        if (show == null) show = 0;
        if (maskBalance == null) maskBalance = false;
        if (coinSymbol == null) coinSymbol = "";

        let showBalance = "";

        if (maskBalance) {
            showBalance = "******";
        } else {
            if (balance == "--") {
                showBalance = "--";
            } else if (show == 0) {
                showBalance = balance.toTrimed(2, decimals);
            } else {
                let n = balance.times(exchangeRate)
                showBalance = n.toTrimed(2, decimals);
            }
        }

        showBalance = coinSymbol + showBalance;

        return (
            <div className={this.props.className}>{showBalance}</div>
        );
    }
}


/**
 * Coin信息面板
 * 
 * props:{
 * 
 *      {CoinSpec} coin: coin的基本信息
 *      {}
 * }
 */
export const CoinPanel = (props) => {
    let { coin, wallet, hideFrozen, onClick, includeFrozen = true } = props;

    const zero = new BigNumber(0);

    //coinBalance [balance, available, frozen]
    const [coinBalance, setBalance] = useState([zero, zero, zero]);

    const coinName = coin == null ? "" : coin.name.toUpperCase();
    const coinFull = coin == null ? "" : coin.fullName;
    const hide = hideFrozen ?? true

    wallet = wallet == null ? WalletAccount.All : wallet;

    const updateWallet = () => {
        if (wallet == WalletAccount.DeFi) {
            WalletService.walletInfo.balance(coin).then(b => {
                setBalance([b, b, zero]);
            });

        } else {
            const b = DataCenter.walletBalance(wallet, coin.id, includeFrozen);
            const a = DataCenter.walletBalance(wallet, coin.id, false);
            const f = DataCenter.walletFrozen(wallet, coin.id);
            setBalance([b, a, f])
        }
    }

    const onDataUpdated = (dataName) => {
        if (dataName == "wallet") {
            updateWallet();
        }
    }

    useEffect(() => {
        JSEvent.on(Events.DataCenter.DATA_UPDATED, onDataUpdated);
        return () => {
            JSEvent.remove(Events.DataCenter.DATA_UPDATED, onDataUpdated);
        }
    }, [wallet])

    useEffect(() => {
        updateWallet();
    }, [wallet, coin])

    const currency = CurrentCurrency();
    const price = DataCenter.getCoinQuotePrice(coin.id, QuoteCoinID, currency.name);

    const [balance, available, frozen] = coinBalance;

    let frozenDom = hide ? "" : <>
        <div className="flex pl-[32px] pt-1">
            <div className="pl-3 text-m text-gray-400 justify-start flex-1">可用</div>
            <div className=" text-m text-gray-400 justify-end">冻结</div>
        </div>

        <div className="flex pl-[32px]">
            <div className="pl-3 text-m justify-start flex-1">{available.toTrimed(2, coin.showDecimals)}</div>
            <div className=" text-m justify-end">{frozen.toTrimed(2, coin.showDecimals)}</div>
        </div>
    </>

    return (
        <div className="flex flex-col w-full pt-3 pb-3" onClick={e => {
            onClick?.({ coin, wallet })
        }}>
            <div className="flex">
                <div className="flex w-[32px] justify-center items-start mr-3">
                    {/* <img className="w-[32px] h-[32px]" src={icon} /> */}
                    <CoinIcon coin={coin} className="w-[32px] h-[32px]" />
                </div>
                <div className="flex flex-col h-full justify-start">
                    <div className="flex justify-start items-center text-lg ">{coinName}</div>
                    <div className="flex justify-start items-center text-m text-gray-400">{coinFull}</div>
                </div>
                <div className="flex flex-col justify-start h-full flex-1 items-end">
                    <div className="text-lg ">{balance.toTrimed(2, coin.showDecimals)}</div>
                    <div className="text-m text-gray-400">{currency.symbol + price.times(balance).toCurrencyFixed(currency.decimals)}</div>
                </div>
            </div>
            {frozenDom}
        </div>
    );
}

export const CoinSelector = ({ currCoin = QuoteCoinID, onCoinSelected }) => {
    const [pop] = usePopWindow();
    const coins = DataCenter.getCoins();

    const [coinSel, setCoinSel] = useState(null);

    const onDataUpdated = (name) => {
        if (name == "symbol") {
            const coin = DataCenter.getCoin(currCoin);
            if (coinSel == null || coin.id != coinSel.id) {
                setCoinSel(coin)
                onCoinSelected?.(coin.id);
            }
        }
    }

    useEffect(() => {
        JSEvent.on(Events.DataCenter.DATA_UPDATED, onDataUpdated)

        const coin = DataCenter.getCoin(currCoin);
        if (coinSel == null || coin.id != coinSel.id) {
            setCoinSel(coin)
            onCoinSelected?.(coin.id);
        }

        return () => {
            JSEvent.remove(Events.DataCenter.DATA_UPDATED, onDataUpdated)
        }
    }, [])

    if (coins.length == 0 || coinSel == null) {
        return <div></div>;
    }


    const onCoinAction = (action) => {
        if (action.coinSel != null) {
            setCoinSel(action.coinSel);
            onCoinSelected?.(action.coinSel.id);
        }
    }

    const CurrSelCoin = () => {
        return <div className="flex truncate flex-row items-center w-full h-auto " onClick={e => {
            pop("pop-coin-select", CoinList, Localize.getMsg(Localize.COMMON.coinList), onCoinAction, {
                coins, coinSelected: coinSel
            })
        }}>
            <CoinIcon coin={coinSel} className="h-6" />
            <div className="pl-2 text-xl">{coinSel.name}</div>
            <div className="pl-1 text-sm text-gray-400 flex-1 items-end flex h-full">({coinSel.fullName})</div>
            <div className="flex justify-end text-gray-500"><ChevronRightIcon className="h-6" /></div>
        </div>
    }

    const CoinItem = ({ item, isSelected }) => {
        return <div className="flex w-full h-full">
            <div className="w-10 flex items-center justify-center">
                <CoinIcon coin={item} className="h-6" />
            </div>
            <div className="text-xl">{item.name}</div>
            <div className="pl-1 text-sm text-gray-400 flex-1 items-end flex h-full">({item.fullName})</div>
        </div>
    }

    const CoinList = ({ coins, coinSelected, onViewAction }) => {
        const [coinSel, setCoinSel] = useState(coinSelected);
        return <div className="flex flex-col w-full">
            <div className="flex flex-col w-full h-52">
                <ListSelector list={coins} currSel={coinSelected.id} onSelect={item => {
                    setCoinSel(item);
                }} listItem={CoinItem} />
            </div>
            <div className="flex flex-col p-4 w-full">
                <Button name="L:COMMON.Confirm" widthStyle="w-full" type="primary" onClicked={e => {
                    onViewAction({ name: "close", coinSel })
                }} />
            </div>
        </div>
    }


    return <div className="flex flex-col w-full h-full">
        <div className="flex-1"></div>
        <CurrSelCoin />
        <div className="flex-1"></div>
    </div>

}


export class CoinSelector1 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currCoin: 1
        }
    }

    render() {

        const coins = DataCenter.getCoins();

        if (coins.length == 0) {
            return <div></div>;
        }

        const cc = DataCenter.getCoin(this.state.currCoin);

        let options = [];

        coins.forEach(c => {
            let style = null;
            if (c.id == cc.id) {
                style = "text-gray-900 bg-gray-300";
            } else {
                style = "text-gray-500"
            }

            options.push(<Listbox.Option
                className={"flex flex-row items-center cursor-default select-none relative py-2 pl-10 pr-4 " + style}
                key={c.id} value={c.id}>
                <div className="h-6 w-10 flex items-center justify-center">
                    <CoinIcon coin={c} className="h-6" />
                </div>
                <div className="text-xl">{c.name}</div>
                <div className="pl-1 text-sm text-gray-400">({c.fullName})</div>
            </Listbox.Option>);
        })

        return (
            <div className='flex items-center flex-1'>
                <div className='flex-1 justify-center items-center '>
                    <Listbox
                        value={cc.id} onChange={e => {
                            this.setState({ currCoin: e })
                            if (this.props.onCoinSelected != null) {
                                this.props.onCoinSelected(e);
                            }
                        }}>
                        <div className="relative mt-1 w-full">
                            <Listbox.Button
                                className="relative w-full py-2 pl-3 pr-10 text-left cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                            >
                                <div className="flex truncate flex-row items-center">
                                    <div className="w-10 flex items-center justify-center">
                                        <CoinIcon coin={cc} className="h-6" />
                                    </div>
                                    <div className="text-xl">{cc.name}</div>
                                    <div className="pl-1 text-sm text-gray-400">({cc.fullName})</div>
                                </div>
                                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <ChevronRightIcon
                                        className="w-5 h-5 text-gray-400"
                                        aria-hidden="true"
                                    />
                                </span>
                            </Listbox.Button>
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-gray-100 rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {options}
                                </Listbox.Options>
                            </Transition>
                        </div>
                    </Listbox>

                </div>
            </div>
        )
    }
}

export class OrderTypeSelector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currOrderType: 0
        }
    }

    render() {

        const orderTypes = OrderTypes;

        let options = [];

        let cc = null;
        orderTypes.forEach(c => {
            let style = null;
            if (c.id == this.state.currOrderType) {
                cc = c;
                style = "text-gray-900 bg-gray-300 font-semibold";
            } else {
                style = "text-gray-500"
            }

            options.push(<Listbox.Option
                className={"flex flex-row items-center cursor-default select-none relative py-2 " + style}
                key={c.id} value={c.id}>
                <div className="text-m flex w-full items-center justify-center">{c.name}</div>
            </Listbox.Option>);
        })

        return (
            <div className='flex items-center'>
                <div className='flex-1 justify-center items-center '>
                    <Listbox
                        value={cc.id} onChange={e => {
                            this.setState({ currOrderType: e })
                            if (this.props.onChange != null) {
                                this.props.onChange(orderTypes[e]);
                            }
                        }}>
                        <div className="relative mt-1 w-full">
                            <Listbox.Button
                                className="relative w-full text-left cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                            >
                                <div className="flex truncate flex-row items-center">
                                    <div className="w-full flex text-m font-semibold items-center justify-center">{cc.name}</div>
                                </div>
                                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <ChevronDownIcon
                                        className="w-5 h-5 text-gray-400"
                                        aria-hidden="true"
                                    />
                                </span>
                            </Listbox.Button>
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-gray-100 rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {options}
                                </Listbox.Options>
                            </Transition>
                        </div>
                    </Listbox>
                </div>
            </div>
        )
    }
}

export function FailedDialog({ show, text, onShown, onHidden }) {
    const [visible, setVisible] = useState(show);
    const shown = () => {
        if (onShown) {
            onShown();
        }
    }

    const hidden = () => {
        setVisible(false);
        if (onHidden) {
            onHidden();
        }
    }

    const startShow = () => {
        setVisible(true);
    }

    const startHide = () => {

    }

    const styles = useSpring({
        from: { opacity: show ? 0 : 0.8 },
        to: { opacity: show ? 0.8 : 0 },
        onStart: {
            opacity: () => {
                if (show) {
                    startShow();
                } else {
                    startHide();
                }
            }
        },
        onRest: {
            opacity: () => {
                if (show) {
                    shown();
                } else {
                    hidden();
                }
            }
        }
    })
    // < div className = "flex justify-center items-center w-full h-full border-2 rounded-full p-2" >

    return visible ?
        <div className=" absolute flex justify-center items-center left-0 top-0 w-screen h-screen " >
            <animated.div className="  justify-center items-center w-40 h-44 rounded-md bg-gray-500 flex flex-col " style={{ ...styles }}>
                <div className="flex justify-center items-center w-20 h-20 border-2 rounded-full p-2">
                    <XIcon className="flex w-20 h-20 justify-center items-center text-white" />
                </div>
                <div className="flex pt-4 text-white text-m px-3">{text}</div>
            </animated.div>
        </div > : ""
}


export function SuccessfulDialog({ show, text, onShown, onHidden }) {
    const [visible, setVisible] = useState(show);
    const shown = () => {
        if (onShown) {
            onShown();
        }
    }

    const hidden = () => {
        setVisible(false);
        if (onHidden) {
            onHidden();
        }
    }

    const startShow = () => {
        setVisible(true);
    }

    const startHide = () => {

    }

    const styles = useSpring({
        from: { opacity: show ? 0 : 0.8 },
        to: { opacity: show ? 0.8 : 0 },
        onStart: {
            opacity: () => {
                if (show) {
                    startShow();
                } else {
                    startHide();
                }
            }
        },
        onRest: {
            opacity: () => {
                if (show) {
                    shown();
                } else {
                    hidden();
                }
            }
        }
    })
    // < div className = "flex justify-center items-center w-full h-full border-2 rounded-full p-2" >

    return visible ?
        <div className=" absolute flex justify-center items-center left-0 top-0 w-screen h-screen " >
            <animated.div className="  justify-center items-center w-36 h-44 rounded-md bg-gray-500 flex flex-col " style={{ ...styles }}>
                <div className="flex justify-center items-center w-20 h-20 border-2 rounded-full p-2">
                    <CheckIcon className="flex w-20 h-20 justify-center items-center text-white" />
                </div>
                <div className="flex pt-4 text-white text-2xl">{text}</div>
            </animated.div>
        </div > : ""
}


/**
 * 
 * @param {show, onShown, onHidden} param0 
 * @returns 
 */
export function LoadingDialog({ show, onShown, onHidden, showBg = true }) {

    const [visible, setVisible] = useState(show);

    const shown = () => {
        if (onShown) {
            onShown();
        }
    }

    const hidden = () => {
        setVisible(false);
        if (onHidden) {
            onHidden();
        }
    }

    const startShow = () => {
        setVisible(true);
    }

    const startHide = () => {

    }

    const styles = useSpring({
        from: { opacity: show ? 0 : 0.8 },
        to: { opacity: show ? 0.8 : 0 },
        onStart: {
            opacity: () => {
                if (show) {
                    startShow();
                } else {
                    startHide();
                }
            }
        },
        onRest: {
            opacity: () => {
                if (show) {
                    shown();
                } else {
                    hidden();
                }
            }
        }
    })


    return visible ?
        <div className=" absolute flex justify-center items-center left-0 top-0 w-full h-screen " >
            <animated.div className={"  justify-center items-center w-20 h-20 rounded-md  flex" + (showBg ? " bg-gray-500" : "")} style={{ ...styles }}>
                {LoadingSVG}
            </animated.div>
        </div > : null
}


export function NumberSelect({ initValue, decimals, placeholder, onValueChange }) {
    const input = useRef();
    const inc = Math.pow(10, -decimals);

    //const [value, setValue] = useState(initValue);

    const setValue = (v) => {
        input.current.value = v == 0 ? "" : v;
    }

    useEffect(() => {
        setValue(parseFloat(initValue).toFixed(decimals));
        //input.current.value = initValue == 0 ? "" : initValue;
    }, [initValue]);


    const addValue = () => {
        const value = input.current.value;
        const v = (parseFloat(value == "" ? 0 : value) + inc).toFixed(decimals)
        setValue(v);
        if (onValueChange)
            onValueChange(v);
    }

    const decValue = () => {
        const value = input.current.value;
        let v = (parseFloat(value == "" ? 0 : value) - inc).toFixed(decimals)
        if (v < 0) v = 0;
        setValue(v == 0 ? "" : v);
        if (onValueChange)
            onValueChange(v);
    }

    return <div className="flex w-full px-2 py-2 bg-gray-100 rounded-md h-12">
        <MinusIcon className=" w-6" onClick={decValue} />
        <div className="flex flex-1 items-center justify-center ">
            <input ref={input} type="number" placeholder={placeholder} className="flex w-full items-center justify-center font-semibold bg-gray-100 text-center outline-none"
                onBlur={e => {
                    let v = parseFloat(e.target.value);
                    if (v < 0) v = 0;
                    setValue(v.toFixed(decimals));
                    if (onValueChange)
                        onValueChange(v);
                }}

                onKeyDown={e => {
                    if (e.key == "Enter") {
                        input.current.blur();
                    }
                }}

                onChange={e => {
                    // let v = parseFloat(e.target.value);
                    // if (v < 0)
                    //     v = 0;
                    // else
                    //     v = e.target.value;
                    //setValue(v);
                    // if (onValueChange)
                    //     onValueChange(v);
                }} />
        </div>
        <PlusIcon className=" w-6" onClick={addValue} />
    </div>
}

export function MarketSymbolPanel({ thumb, symbolSpec, onClick }) {
    const { baseCoin, quoteCoin, quoteCoinDecimal } = symbolSpec;
    const { close, lastClose, open } = thumb;

    const cc = CurrentCurrency();

    let { priceChange, percentChange, percent, percentSign, currencyPrice } = DataCenter.getCoinPairCurrentState(symbolSpec);

    let priceDom = <div className={`flex items-end text-xl font-semibold text-price-up`}>{close.toFixed(quoteCoinDecimal)}</div>
    if (priceChange == "down") {
        priceDom = <div className={`flex items-end text-xl font-semibold text-price-down`}>{close.toFixed(quoteCoinDecimal)}</div>
    }

    let percentDom = <div className={`flex justify-center rounded-md items-center w-full h-full text-lg text-white bg-price-up`}>
        {percentSign}{percent.toFixed(2)}%
    </div>
    if (percentChange == "down") {
        percentDom = <div className={`flex justify-center rounded-md items-center w-full h-full text-lg text-white bg-price-down`}>
            {percentSign}{percent.toFixed(2)}%
        </div>
    }

    return <div className="w-full mb-3 flex flex-row flex-1" onClick={e => { if (onClick != null) onClick(); }}>
        <div className="flex flex-col w-28">
            <div className="flex">
                <div className="flex items-end text-xl font-semibold text-black">{baseCoin.name}</div>
                <div className="flex items-end text-m text-gray-400 pl-1">/{quoteCoin.name}</div>
            </div>
            <div className="flex text-gray-400 pt-1">成交量 {thumb.volume}</div>
        </div>
        <div className="flex flex-col flex-1 items-end">
            {priceDom}
            <div className="flex text-gray-400 pt-1">{cc.symbol}{currencyPrice.toCurrencyFixed(cc.decimals, 6)}</div>
        </div>
        <div className="flex w-32 justify-end items-center pt-1 pr-1 pb-1 pl-5">
            {percentDom}
        </div>
    </div>
}

export function MarketSymbolLitePanel({ thumb, symbolSpec, onClick }) {
    const { baseCoin, quoteCoin, quoteCoinDecimal } = symbolSpec;
    const { close, lastClose, open } = thumb;

    const cc = CurrentCurrency();

    let { priceChange, percentChange, percent, percentSign, currencyPrice } = DataCenter.getCoinPairCurrentState(symbolSpec);

    let priceDom = <div className={`flex items-end text-price-up`}>{close.toFixed(quoteCoinDecimal)}</div>
    if (priceChange == "down") {
        priceDom = <div className={`flex items-end text-price-down`}>{close.toFixed(quoteCoinDecimal)}</div>
    }

    let percentDom = <div className={`flex justify-center rounded-md items-center text-sm h-full text-price-up`}>
        {percentSign}{percent.toFixed(2)}%
    </div>
    if (percentChange == "down") {
        percentDom = <div className={`flex justify-center rounded-md items-center text-sm h-full text-price-down`}>
            {percentSign}{percent.toFixed(2)}%
        </div>
    }

    return <div className="w-full mb-3 flex flex-row flex-1" onClick={e => { if (onClick != null) onClick(); }}>
        <div className="flex flex-col w-28">
            <div className="flex">
                <div className="flex items-end font-semibold text-black">{baseCoin.name}</div>
                <div className="flex items-end text-sm text-gray-400 pl-1">/{quoteCoin.name}</div>
            </div>
        </div>
        <div className="flex flex-col flex-1 items-end">
            {priceDom}
            {percentDom}
        </div>
    </div>
}

export function SymbolSelectView(props) {

    const { symbol } = DataCenter;
    let marketData = [];

    if (DataCenter.hasThumbData()) {
        const l = symbol.exchangePairs.length;
        for (let i = 0; i < l; i++) {
            const coinSymbol = symbol.exchangePairs[i];
            const coinThumb = DataCenter.getCoinPairThumb(coinSymbol.id);
            marketData.push(
                <MarketSymbolLitePanel
                    key={coinSymbol.id}
                    thumb={coinThumb}
                    symbolSpec={coinSymbol}
                    onClick={() => {
                        props.fireViewAction({ name: "click", value: coinSymbol });
                    }}
                />)
            // if (i < l - 1)
            //     marketData.push(<div className='w-full h-[1px] bg-gray-100 mb-3' key={coinSymbol.id + 100000000} />)
        }
    }

    return <div className="flex h-full w-full">
        <div className=" w-4/5 bg-white h-full rounded-r-2xl">
            <div className={"w-full  flex flex-1 flex-col  p-4"}>
                <div className=' w-full h-8 flex flex-col items-start justify-center' onClick={() => props.fireViewAction({ name: "close" })}>
                    <ArrowNarrowLeftIcon className="h-6 text-gray-500"
                        aria-hidden="true" /></div>
                <div className=' text-2xl font-semibold mt-2'>
                    行情
                </div>
                <div className='w-full h-[1px] bg-gray-100 mb-3 mt-2' />
                <div className="flex w-full text-gray-400 text-sm mb-3">
                    <div>名称</div>
                    <div className="flex flex-1 justify-end">最新价格/24h涨幅</div>
                </div>
                {marketData}
            </div>
        </div>
        <div className="flex-1 h-full" onClick={() => props.fireViewAction({ name: "close" })}>

        </div>
    </div>
}

export const tradingOrderReducer = (state, action) => {
    switch (action.type) {
        case "set":
            return action.value;
        case "place":
            return [action.value, ...state];
        case "cancel":
            if (Array.isArray(action.value)) {
                //批量删除
                action.value.forEach(o => {
                    for (const key in state) {
                        if (state[key].id == o.id) {
                            state.splice(key, 1);
                            break;
                        }
                    }
                });
            } else {
                for (const key in state) {
                    if (state[key].id == action.value.id) {
                        state.splice(key, 1);
                        break;
                    }
                }
            }
            return [...state];
        case "update":
            for (const key in state) {
                if (state[key].id == action.value.id) {
                    if (action.value.orderStatus != OrderStatus.Trading) {
                        //订单完成了，移除
                        state.splice(key, 1);
                    } else {
                        state[key] = action.value;
                    }
                    break;
                }
            }
            return [...state];
    }
    return state;
}

export function OrderDetailView(props) {
    let { order } = props;
    var symbol = DataCenter.getCoinPair(order.symbolId);

    const ot = GetOrderType(order.orderType);
    const price = BigNumber(order.price).toFixed(symbol.quoteCoinDecimal);
    const count = BigNumber(order.amount).toFixed(symbol.baseCoinDecimal);
    const tradedCount = BigNumber(order.tradeAmount).toFixed(symbol.baseCoinDecimal);
    const createTime = LongTimeToFullStr(order.createTime);
    const tradedTime = LongTimeToFullStr(order.tradedTime);
    const fTradeAmount = parseFloat(order.tradeAmount);
    const fAmount = parseFloat(order.amount);

    const avgPrice = fTradeAmount == 0 ? "0" : BigNumber(order.turnover / order.tradeAmount).toFixed(symbol.quoteCoinDecimal);


    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState([]);

    const init = () => {
        request(API.exchange_getOrderDetail(DataCenter.accountId, order.id)).then(ret => {
            //console.log(ret.retObject);
            setDetails(ret.retObject);
        });
    }

    useEffect(() => {
        init();
    }, []);


    let dealDom = <div className=" text-price-up flex items-center">完全成交 (100%)</div>

    const dealPercent = (fTradeAmount / fAmount * 100).toFixed(0) + "%";

    let detailItems = [];
    let id = 0;
    details.forEach(d => {
        detailItems.push(<OrderDetailPanel key={id++} order={order} detail={d} symbol={symbol} />);
    })

    if (order.orderStatus == OrderStatus.Canceled) {
        dealDom = <div className=" text-gray-400 flex items-center">已撤销 ({dealPercent})</div>
    } else if (order.orderStatus == OrderStatus.Completed && order.amount != order.tradeAmount) {
        dealDom = <div className=" text-price-down flex items-center">{
            fTradeAmount > 0 ? "部分成交 " : "未成交 "
        }({dealPercent})</div>
    }


    return <div className="flex w-full h-full flex-col bg-gray-100">
        <div className={"w-full flex flex-row px-4 pt-4 bg-white"}>
            <div className='h-8 w-8 flex items-start justify-center' onClick={() => props.fireViewAction({ name: "close" })}>
                <ArrowNarrowLeftIcon className="h-6 text-gray-500"
                    aria-hidden="true" /></div>
            <div className='flex-1 flex font-semibold pr-8 justify-center'>
                订单详情
            </div>
        </div>
        <div className="flex w-full bg-gray-100 pt-4 pb-2 flex-col">
            <div className="flex w-full text-gray-400 justify-center">币对</div>
            <div className="flex w-full font-semibold text-lg justify-center">{symbol.baseCoin.name}/{symbol.quoteCoin.name}</div>
            <div className="flex w-full justify-center items-center">
                <CheckCircleIcon className=" text-gray-400 w-5 h-5 mr-1" />
                <div className="text-sm">{dealDom}</div>
            </div>
        </div>
        <div className="flex w-full flex-col rounded-t-2xl bg-white px-4 pt-4 text-sm">
            <div className="flex w-full">
                <div className=" text-gray-400">订单号</div>
                <div className="flex flex-1 justify-end">{order.id}</div>
            </div>
            <div className="flex w-full pt-2">
                <div className=" text-gray-400">类型</div>
                <div className="flex flex-1 justify-end">{
                    order.orderAction == 1 ?
                        <div className="flex text-price-up text-m">{ot.short}/买入</div> :
                        <div className="flex text-price-down text-m">{ot.short}/卖出</div>
                }</div>
            </div>
            <div className="flex w-full pt-2">
                <div className=" text-gray-400">成交数量</div>
                <div className="flex flex-1 justify-end">
                    <div>{trimZero(order.tradeAmount)}</div>
                    <div className="text-gray-400">/{trimZero(order.amount)}</div>
                </div>
            </div>
            <div className="flex w-full pt-2">
                <div className=" text-gray-400">均价/价格</div>
                <div className="flex flex-1 justify-end">
                    <div>{trimZero(avgPrice)}</div>
                    <div className="text-gray-400">/{trimZero(price)}</div>
                </div>
            </div>
            <div className="w-full h-[1px] bg-gray-100 m-4"></div>

            <div className="flex w-full">
                <div className=" text-gray-400">手续费</div>
                <div className="flex flex-1 justify-end">
                    <div>
                        {fTradeAmount == 0 ? "--" :
                            trimZero(order.serviceFee) + " " + (order.orderAction == 1 ? symbol.baseCoin.name : symbol.quoteCoin.name)
                        }
                    </div>
                </div>
            </div>
            <div className="flex w-full pt-2">
                <div className=" text-gray-400">成交额</div>
                <div className="flex flex-1 justify-end">
                    <div>
                        {fTradeAmount == 0 ? "--" :
                            trimZero(order.turnover) + " " + symbol.quoteCoin.name
                        }
                    </div>
                </div>
            </div>
            <div className="flex w-full pt-2">
                <div className=" text-gray-400">下单时间</div>
                <div className="flex flex-1 justify-end">
                    <div>{LongTimeToFullStr(order.createTime)}</div>
                </div>
            </div>
            <div className="flex w-full pt-2 pb-2">
                <div className=" text-gray-400">
                    {order.orderStatus == OrderStatus.Canceled ? "撤销时间" : "成交时间"}
                </div>
                <div className="flex flex-1 justify-end">
                    <div>{LongTimeToFullStr(order.tradedTime)}</div>
                </div>
            </div>
        </div>
        <div className="flex flex-col w-full mt-1 bg-white flex-1 py-4 pl-4 pr-2">
            <div className="flex w-full font-semibold pb-2">成交详情</div>
            <div className="flex w-full flex-1 basis-0 overflow-y-auto pr-2">
                <div className="flex w-full flex-col">
                    {detailItems}
                </div>
            </div>
        </div>
    </div>
}

function OrderDetailPanel({ order, symbol, detail }) {
    return <div className="flex flex-col w-full text-sm">
        <div className="flex w-full">
            <div className="flex text-gray-400">日期</div>
            <div className="flex flex-1 justify-end ">{LongTimeToFullStr(detail.dealDate)}</div>
        </div>
        <div className="flex w-full pt-2">
            <div className="flex text-gray-400">价格</div>
            <div className="flex flex-1 justify-end ">{trimZero(detail.dealPrice)}</div>
        </div>
        <div className="flex w-full pt-2">
            <div className="flex text-gray-400 ">数量</div>
            <div className="flex flex-1 justify-end ">{trimZero(detail.dealAmount)}</div>
        </div>
        <div className="flex w-full pt-2">
            <div className="flex text-gray-400 ">手续费</div>
            <div className="flex flex-1 justify-end ">{
                trimZero(detail.serviceFee) + " "}
                {order.orderAction == 1 ? symbol.baseCoin.name : symbol.quoteCoin.name}</div>
        </div>
        <div className="flex w-full pt-2">
            <div className="flex text-gray-400 ">角色</div>
            <div className="flex flex-1 justify-end ">{detail.role == 1 ? "Maker" : "Taker"}</div>
        </div>
        <div className="w-full h-[1px] my-2 bg-gray-100"></div>
    </div>
}

export function OrderListView(props) {
    let { symbol } = props;

    const onOrderPlaced = order => {
        //setCurrOrder([order, ...currOrder]);
        currOrderDispatch({ type: "place", value: order });
    }

    const onOrderCanceled = order => {
        currOrderDispatch({ type: "cancel", value: order });
    }

    const onOrderUpdated = order => {
        currOrderDispatch({ type: "update", value: order });
    }

    const init = () => {
        request(API.exchange_tradingOrders(DataCenter.accountId, -1)).then(ret => {
            currOrderDispatch({ type: "set", value: ret.retObject });
        });

        //监听订单消息
        JSEvent.on(Events.Trade.ORDER_PLACED, onOrderPlaced);
        JSEvent.on(Events.Trade.ORDER_CANCELED, onOrderCanceled);
        JSEvent.on(Events.Trade.ORDER_UPDATED, onOrderUpdated);

    }

    const unmount = () => {
        JSEvent.remove(Events.Trade.ORDER_PLACED, onOrderPlaced);
        JSEvent.remove(Events.Trade.ORDER_CANCELED, onOrderCanceled);
        JSEvent.remove(Events.Trade.ORDER_UPDATED, onOrderUpdated);
    }

    const [currOrder, currOrderDispatch] = useReducer(tradingOrderReducer, []);
    const [orderHistory, setOrderHistory] = useState([]);
    const [orderTraded, setOrderTraded] = useState([]);
    const [currOrderType, setCurrOrderType] = useState(0);
    const [failedMsg, setFailedMsg] = useState(null);
    const [showError, setShowError] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        init();
        return () => unmount();
    }, [])

    useEffect(() => {
        switch (currOrderType) {
            case 0:
                break;
            case 1:
                if (orderHistory.length == 0) {
                    const p = {
                        symbolId: -1,
                        orderAction: -1,
                        includeCanceled: true,
                        scope: SearchTimeScope.OneWeek,
                    }
                    request(API.exchange_searchOrder(DataCenter.accountId, p)).then(ret => {
                        if (!ret.error) {
                            setOrderHistory(ret.retObject);
                        }
                    })
                }
                break;
            case 2:
                if (orderTraded.length == 0) {
                    const p = {
                        symbolId: -1,
                        orderAction: -1,
                        includeCanceled: false,
                        scope: SearchTimeScope.OneWeek,
                    }
                    request(API.exchange_searchOrder(DataCenter.accountId, p)).then(ret => {
                        if (!ret.error) {
                            setOrderTraded(ret.retObject);
                        }
                    })
                }
                break;

        }
    }, [currOrderType])


    const OrderTypes = [
        { type: 0, name: "当前委托", walletId: WalletAccount.All },
        { type: 1, name: "历史委托", walletId: WalletAccount.GameAccount },
        { type: 2, name: "成交历史", walletId: WalletAccount.TradeAccount },
    ]

    const requestCancelOrder = (order) => {
        setLoading(true);
        request(API.exchange_cancelOrder({
            account: DataCenter.accountId,
            orderId: order == null ? -1 : order.id,
            symbolId: order == null ? -1 : order.symbolId
        })).then(ret => {
            setLoading(false);
            if (ret.error) {
                const msg = ErrorCode.msg(ret.code);
                setFailedMsg(msg);
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                }, 2000);
            }
        });
    }

    const orderPanel = (order) => {

        const pairSpec = DataCenter.getCoinPair(order.symbolId);
        const ot = GetOrderType(order.orderType);
        const price = BigNumber(order.price).toFixed(pairSpec.quoteCoinDecimal);
        const count = BigNumber(order.amount).toFixed(pairSpec.baseCoinDecimal);
        const tradedCount = BigNumber(order.tradeAmount).toFixed(pairSpec.baseCoinDecimal);

        return <div key={order.id} className="flex flex-row w-full px-4 py-1 text-m">
            <div className="flex flex-col w-[90px] justify-start items-center">
                {
                    order.orderAction == 1 ?
                        <div className="flex text-price-up text-m">{ot.short}/买入</div> :
                        <div className="flex text-price-down text-m">{ot.short}/卖出</div>
                }
                {
                    order.orderAction == 1 ?
                        <div className="text-price-up">{(tradedCount / count * 100).toFixed(0)}%</div> :
                        <div className="text-price-down">{(tradedCount / count * 100).toFixed(0)}%</div>
                }

            </div>
            <div className="flex flex-col flex-1 justify-start items-start pl-2">
                <div className="flex font-semibold">{pairSpec.baseCoin.name}/{pairSpec.quoteCoin.name}</div>
                <div className="flex flex-row pt-1">
                    <div className="flex text-gray-400">数量</div>
                    <div className="flex flex-1 pl-5">{tradedCount}/{count}</div>
                </div>
                <div className="flex flex-row">
                    <div className="flex text-gray-400">价格</div>
                    <div className="flex flex-1 pl-5">{price}</div>
                </div>
            </div>
            <div className="flex flex-col items-end">
                <div className="flex text-gray-400 text-m">{LongTimeToDateStr(order.createTime)}</div>
                <div className="flex text-gray-400 text-m">{LongTimeToTimeStr(order.createTime)}</div>
                <div className="flex pt-3"><Button onClicked={() => requestCancelOrder(order)} type="primary" name="撤单" widthStyle="w-24" /></div>
            </div>
        </div>
    }

    const orderHistoryPanel = (order, onClick) => {
        const pairSpec = DataCenter.getCoinPair(order.symbolId);
        const ot = GetOrderType(order.orderType);
        const price = BigNumber(order.price).toFixed(pairSpec.quoteCoinDecimal);
        const count = BigNumber(order.amount).toFixed(pairSpec.baseCoinDecimal);
        const tradedCount = BigNumber(order.tradeAmount).toFixed(pairSpec.baseCoinDecimal);
        const createTime = LongTimeToFullStr(order.createTime);
        const tradedTime = LongTimeToFullStr(order.tradedTime);

        const fTradeAmount = parseFloat(order.tradeAmount);

        const avgPrice = fTradeAmount == 0 ? "0" : BigNumber(order.turnover / order.tradeAmount).toFixed(pairSpec.quoteCoinDecimal);


        let dealStr = "完全成交";
        let dealStrClr = " text-price-up"
        if (order.orderStatus == OrderStatus.Canceled) {
            dealStr = "已撤销";
            dealStrClr = " text-gray-400"
        } else if (order.amount != order.tradeAmount) {
            if (fTradeAmount == 0) {
                dealStr = "未成交";
            } else {
                dealStr = "部分成交";
            } dealStrClr = " text-price-down"
        }

        let dealDom = <div className={"text-sm rounded-md px-2 bg-gray-100 " + dealStrClr}>
            {dealStr}
        </div>;

        if (order.orderStatus == OrderStatus.Canceled && fTradeAmount > 0) {
            dealDom = <>
                <div className={"text-sm rounded-md px-2 bg-gray-100 text-price-down mr-1"}>
                    部分成交
                </div>
                {dealDom}
            </>;
        }

        return <div key={order.id} className="w-full flex flex-col px-4 pt-2" onClick={() => {
            if (onClick != null) onClick(order)
        }}>
            <div className="w-full flex items-center">
                <div className="flex font-semibold">{pairSpec.baseCoin.name}/{pairSpec.quoteCoin.name}</div>
                <div className="flex flex-1 text-sm justify-end text-gray-400">{createTime}</div>
                <div className="w-6 text-gray-400 flex justify-end"><ChevronRightIcon className="w-4 h-4" /></div>
            </div>
            <div className="w-full flex items-center">
                {
                    order.orderAction == 1 ?
                        <div className="flex text-price-up text-m font-semibold">{ot.short}/买入</div> :
                        <div className="flex text-price-down text-m font-semibold">{ot.short}/卖出</div>
                }
                <div className="flex flex-1 justify-end">
                    {dealDom}
                </div>

            </div>
            <div className="w-full flex items-center pt-1">
                <div className="text-m text-gray-400">数量</div>
                <div className="text-m flex flex-1 justify-end">
                    <div className="">{trimZero(order.tradeAmount)}</div>
                    <div className="text-gray-400">/{trimZero(order.amount)}</div>
                </div>
            </div>
            <div className="w-full flex items-center">
                <div className="text-m text-gray-400">价格</div>
                <div className="text-m flex flex-1 justify-end">
                    <div>{trimZero(avgPrice)}</div>
                    <div className="text-gray-400">/{trimZero(price)}</div>
                </div>
            </div>
        </div>
    }

    const onOrderHistoryClicked = o => {
        props.fireViewAction({ name: "click", value: o });
    }

    const orderTradedPanel = (order) => {
        const pairSpec = DataCenter.getCoinPair(order.symbolId);
        const ot = GetOrderType(order.orderType);
        const price = BigNumber(order.price).toFixed(pairSpec.quoteCoinDecimal);
        const count = BigNumber(order.amount).toFixed(pairSpec.baseCoinDecimal);
        const tradedCount = BigNumber(order.tradeAmount).toFixed(pairSpec.baseCoinDecimal);
        const createTime = LongTimeToFullStr(order.createTime);
        const tradedTime = LongTimeToFullStr(order.tradedTime);

        const fTradeAmount = parseFloat(order.tradeAmount);

        const avgPrice = fTradeAmount == 0 ? "0" : BigNumber(order.turnover / order.tradeAmount).toFixed(pairSpec.quoteCoinDecimal);

        return <div key={order.id} className="w-full flex flex-col px-4 pt-2 text-m " >
            <div className="w-full flex items-center">
                <div className="flex font-semibold">{pairSpec.baseCoin.name}/{pairSpec.quoteCoin.name}</div>
                <div className="flex flex-1 text-sm justify-end text-gray-400">{createTime}</div>
            </div>
            <div className="w-full flex items-center py-1">
                {
                    order.orderAction == 1 ?
                        <div className="flex text-price-up font-semibold">买入</div> :
                        <div className="flex text-price-down font-semibold">卖出</div>
                }
            </div>
            <div className="w-full flex items-center">
                <div className="text-m text-gray-400">价格({pairSpec.quoteCoin.name})</div>
                <div className="text-m flex flex-1 justify-end">
                    <div>{trimZero(avgPrice)}</div>
                </div>
            </div>
            <div className="w-full flex items-center pt-1">
                <div className="text-m text-gray-400">成交数量({pairSpec.baseCoin.name})</div>
                <div className="text-m flex flex-1 justify-end">
                    <div className="">{trimZero(order.tradeAmount)}</div>
                </div>
            </div>
            <div className="w-full flex items-center pt-1">
                <div className="text-m text-gray-400">手续费(
                    {order.orderAction == 1 ? pairSpec.baseCoin.name : pairSpec.quoteCoin.name}
                    )</div>
                <div className="text-m flex flex-1 justify-end">
                    <div className="">{trimZero(order.serviceFee)}</div>
                </div>
            </div>
            <div className="w-full flex items-center pt-1">
                <div className="text-m text-gray-400">总量({pairSpec.quoteCoin.name})</div>
                <div className="text-m flex flex-1 justify-end">
                    <div className="">{trimZero(order.turnover)}</div>
                </div>
            </div>
        </div>
    }

    let panel = "";
    switch (currOrderType) {
        case 0:
            let items = [];
            currOrder.forEach(o => {
                items.push(orderPanel(o))
                items.push(<div key={10000000 + o.id} className="w-full px-4 py-4"><div className="w-full h-[1px] bg-gray-100"></div></div>)
            });
            panel = <>{items}</>
            break;
        case 1:
            let histories = [];
            orderHistory.forEach(o => {
                histories.push(orderHistoryPanel(o, onOrderHistoryClicked));
                histories.push(<div key={10000000 + o.id} className="w-full px-4 py-4"><div className="w-full h-[1px] bg-gray-100"></div></div>)
            })
            panel = <>{histories}</>
            break;
        case 2:
            let tradeds = [];
            orderTraded.forEach(o => {
                if (parseFloat(o.tradeAmount) > 0) {
                    tradeds.push(orderTradedPanel(o));
                    tradeds.push(<div key={10000000 + o.id} className="w-full px-4 py-4"><div className="w-full h-[1px] bg-gray-100"></div></div>)
                }
            })
            panel = <>{tradeds}</>
            break;
    }


    return <div className="flex h-full w-full bg-white flex-col">
        <div className={"w-full flex flex-row px-4 pt-4"}>
            <div className='h-8 w-8 flex items-start justify-center' onClick={() => props.fireViewAction({ name: "close" })}>
                <ArrowNarrowLeftIcon className="h-6 text-gray-500"
                    aria-hidden="true" /></div>
            <div className='flex-1 flex font-semibold pr-8 justify-center'>
                委托
            </div>
        </div>
        <div className="flex flex-1 w-full flex-col">
            <TabMenu tabItems={OrderTypes} onItemChanged={(type, item) => { setCurrOrderType(type) }} />
            <div className="flex w-full h-[1px] bg-gray-100"></div>
            <div className="flex w-full flex-1 flex-col basis-0 overflow-y-auto">
                <div className="flex w-full flex-1 flex-col">
                    {panel}
                </div>
            </div>
        </div>
        <LoadingDialog show={loading} />
        <FailedDialog show={showError} text={failedMsg} />
    </div >
}


const _FormInput = ({ name, value, suffix, validInput, onReset, placeholder, showClearIcon = true, type = "text", children }, ref) => {
    const [hasValue, setHasValue] = useState(value != null && value != "");
    const [error, setError] = useState("")
    const refInput = useRef();

    useImperativeHandle(ref, () => ({
        validateInput,
        setValue,

    }))

    const setValue = (value) => {
        setHasValue(value != null && value != "");
        refInput.current.value = value;
        return validateInput(value);
    }

    const validateInput = () => {
        return validate(refInput.current.value)
    }


    const onChange = e => {
        const v = e.target.value;
        setHasValue(v != "");
    }

    const clearInput = e => {
        if (refInput != null) {
            refInput.current.value = "";
            setHasValue(false);
            setError("");
            if (onReset) {
                onReset();
            }
        }
    }

    const onKeyDown = e => {
        if (e.code == "Enter" || e.code == "NumpadEnter") {
            validate(refInput.current.value);
        }
    }

    const validate = (value) => {
        let err = "";
        if (validInput != null) {
            const errMsg = validInput(name, value);
            if (errMsg == null || errMsg == "") {
                err = "";
            } else {
                err = errMsg;
            }
        } else {
            err = "";
        }
        setError(err);
        return { valid: err == "", value: value };
    }

    let inputStyle = "";
    if (error == "") {
        inputStyle += ""
    } else {
        inputStyle += " border-red-700 ";
    }

    return <div className="flex flex-col w-full">
        <div className={"w-full justify-center items-center flex bg-gray-100 rounded-md border-[1px] border-gray-100 overflow-x-auto " + inputStyle}>
            <input ref={refInput} type={type} placeholder={placeholder}
                className="bg-gray-100 rounded-md flex-1 px-3 py-2 outline-none"
                onChange={onChange}
                onKeyDown={onKeyDown}
                onBlur={e => { validate(e.target.value) }}
            />
            <div className="h-full flex items-center">
                <XCircleIcon hidden={!hasValue || !showClearIcon} className="w-6 h-6 pr-1 text-gray-400" onClick={clearInput} />
                <div className="pr-1">{suffix}</div>
            </div>

            {/* <div className={" absolute flex justify-end " + (hasValue && showClearIcon) ? "right-10" : "right-2"} >{suffix}</div>
            <XCircleIcon hidden={!hasValue || !showClearIcon} className=" absolute w-6 h-6 right-2 text-gray-400" onClick={clearInput} /> */}
            {showClearIcon ? "" : children}
        </div>
        <div className="text-red-700 text-sm pt-1" hidden={error == ""}>{error}</div>
    </div >
}

export const FormInput = forwardRef(_FormInput)


export const TotalBalanceViewWithDefi = ({ balance, quoteCoin }) => {
    const [totalBalance, setTotalBalance] = useState("--");
    const updateTotalBalance = () => {
        const walletInfo = WalletService.walletInfo;
        if (walletInfo == null) {
            setTotalBalance("--");
        } else {
            setTotalBalance(walletInfo.totalBalance())
        }
    }

    const onDataUpdate = (dataName) => {
        if (dataName == "quotePrice") {
            updateTotalBalance();
        }
    }

    const onBalanceUpdate = ({ token, balance }) => {
        updateTotalBalance();
    }

    useEffect(() => {
        JSEvent.on(Events.DataCenter.DATA_UPDATED, onDataUpdate);
        JSEvent.on(Events.Wallet.BalanceUpdate, onBalanceUpdate);
        updateTotalBalance();
        return () => {
            JSEvent.remove(Events.DataCenter.DATA_UPDATED, onDataUpdate)
            JSEvent.remove(Events.Wallet.BalanceUpdate, onBalanceUpdate);
        }
    }, [])


    const showDecimals = quoteCoin == null ? 0 : quoteCoin.showDecimals;
    const quoteName = quoteCoin == null ? "" : quoteCoin.name;
    const currency = CurrentCurrency();
    let prize = 0;
    if (quoteCoin != null) {
        prize = DataCenter.getQuotePrice(quoteCoin.id, currency.name).toNumber();
    }

    const b = totalBalance == "--" ? balance : balance.plus(totalBalance);

    return <div className='flex flex-col'>
        <div className='text-gray-500 text-m'>{Localize.getMsg(Localize.COMMON.equityValue)}({quoteName})</div>
        <div className='flex items-center'>
            <BalanceLabel className='text-3xl'
                balance={b} decimals={showDecimals} />
            <div className='flex pl-1 text-gray-500'>≈
                <BalanceLabel className='pl-1'
                    balance={b} decimals={currency.decimals} show={1}
                    exchangeRate={prize} coinSymbol={currency.symbol} />
            </div>
        </div>
    </div>
}

export const InformationBar = ({ text, onClick }) => {

    if (text.startsWith("L:")) {
        text = text.slice(2);
        text = Localize.getMsgByPath(text);
    }

    return <div className="w-full flex text-sm rounded-md text-link justify-start items-center mt-4 px-4 py-2 bg-link bg-opacity-10"
        onClick={e => { if (onClick != null) onClick() }}>
        <InformationCircleIcon className="w-6 h-6 pr-1" />{text}
    </div>
}

export const ErrorBar = ({ text, onClick }) => {
    return <div className="w-full flex text-sm rounded-md text-clr-wrong justify-start items-center mt-4 px-4 py-2 bg-clr-wrong bg-opacity-10"
        onClick={e => { if (onClick != null) onClick() }}>
        <XCircleIcon className="w-6 h-6 pr-1" /><div className="flex-1">{text}</div>
    </div>
}

export const CheckBar = ({ text, className, checked, onChange }) => {
    const [isChecked, setChecked] = useState(checked);

    useEffect(() => {
        setChecked(checked);
    }, [checked])

    const onClick = e => {
        const c = !isChecked;
        setChecked(c);
        onChange(c);
    }

    const iconClr = isChecked ? "text-clr-right" : "text-gray-400"

    return <div className={" flex items-center " + className} onClick={onClick}>
        <CheckCircleIcon className={"h-full py-[2px] " + iconClr} />
        <div className="flex items-center pl-1">{text}</div>
    </div>
}



export const WalletAccount_Pop_List = ({ onViewAction, walletAccount, coin, includeDeFi = true, showBalance = true }) => {
    const [list, setList] = useState([]);
    const [item, setItem] = useState(null);

    useEffect(() => {
        const list = [];
        for (const wa in [WalletAccount.TradeAccount, WalletAccount.GameAccount]) {
            const item = {
                id: wa,
                name: WAIdToName(wa),
                balance: showBalance ? DataCenter.walletBalance(wa, coin.id, false) : ""
            }
            list.push(item);
            if (wa == walletAccount) {
                setItem(item);
            }
        }

        const { walletInfo } = WalletService;
        if (walletInfo != null && includeDeFi && showBalance) {
            walletInfo.balance(coin).then(
                ret => {
                    const item = { id: WalletAccount.DeFi, name: WAIdToName(WalletAccount.DeFi), balance: ret };
                    list.push(item)
                    if (walletAccount == WalletAccount.DeFi) {
                        setItem(item);
                    }
                    setList(list);
                }
            );
        } else {
            setList(list);
        }

    }, [walletAccount])


    return <div className="flex flex-col w-full max-h-72 h-full overflow-hidden">
        <ListSelector list={list} currSel={walletAccount}
            onSelect={e => {
                setItem(e);
            }}
            listItem={({ item, isSelected }) => {
                const balanceTip = showBalance ?
                    `- ${Localize.getMsg(Localize.COMMON.Balance)} ${item.balance == '--' ? "0.00" : item.balance.toTrimed(2)} ${coin.name}`
                    : "";
                const className = isSelected ? "flex font-semibold" : "flex"
                return <div className={className}>{item.name} {balanceTip}</div>
            }} />
        <Button type="primary" name="确定" widthStyle="w-full"
            className="text-lg font-semibold mt-3" padding="py-2"
            onClicked={e => {
                onViewAction({ name: "select", value: item })
                onViewAction("close")
            }} />
    </div>
}